import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, MessageSquare, Smartphone, Mail, Volume2, Loader2, Save } from 'lucide-react'

interface NotificationsModalProps {
  isOpen: boolean
  onClose: () => void
}

interface NotificationSettings {
  push_enabled: boolean
  email_enabled: boolean
  sms_enabled: boolean
  sound_enabled: boolean
  payment_alerts: boolean
  reminder_alerts: boolean
  marketing: boolean
}

const getAuthToken = () => localStorage.getItem('niki_access_token')

export function NotificationsModal({ isOpen, onClose }: NotificationsModalProps) {
  const [settings, setSettings] = useState<NotificationSettings>({
    push_enabled: true,
    email_enabled: true,
    sms_enabled: false,
    sound_enabled: true,
    payment_alerts: true,
    reminder_alerts: true,
    marketing: false,
  })
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadSettings()
    }
  }, [isOpen])

  const loadSettings = async () => {
    try {
      const res = await fetch('/api/v1/settings/notifications', {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        const data = await res.json()
        setSettings(data)
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await fetch('/api/v1/settings/notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(settings),
      })

      if (res.ok) {
        setSuccess(true)
        setTimeout(() => {
          setSuccess(false)
          onClose()
        }, 1500)
      }
    } catch (err) {
      console.error('Failed to save settings:', err)
    } finally {
      setSaving(false)
    }
  }

  const Toggle = ({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`w-12 h-6 rounded-full transition-colors relative ${
        enabled ? 'bg-primary' : 'bg-white/20'
      }`}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  )

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#1a2e1a] rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#1a2e1a]">
            <h2 className="text-xl font-semibold text-white">Notifications</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Delivery Methods */}
            <div>
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">
                Delivery Methods
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-white/60" />
                    <div>
                      <p className="text-white">Push Notifications</p>
                      <p className="text-sm text-white/50">Receive alerts on your device</p>
                    </div>
                  </div>
                  <Toggle
                    enabled={settings.push_enabled}
                    onChange={(v) => setSettings({ ...settings, push_enabled: v })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-white/60" />
                    <div>
                      <p className="text-white">Email Notifications</p>
                      <p className="text-sm text-white/50">Get updates via email</p>
                    </div>
                  </div>
                  <Toggle
                    enabled={settings.email_enabled}
                    onChange={(v) => setSettings({ ...settings, email_enabled: v })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-white/60" />
                    <div>
                      <p className="text-white">SMS Notifications</p>
                      <p className="text-sm text-white/50">Receive text messages</p>
                    </div>
                  </div>
                  <Toggle
                    enabled={settings.sms_enabled}
                    onChange={(v) => setSettings({ ...settings, sms_enabled: v })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Volume2 className="w-5 h-5 text-white/60" />
                    <div>
                      <p className="text-white">Sound</p>
                      <p className="text-sm text-white/50">Play notification sounds</p>
                    </div>
                  </div>
                  <Toggle
                    enabled={settings.sound_enabled}
                    onChange={(v) => setSettings({ ...settings, sound_enabled: v })}
                  />
                </div>
              </div>
            </div>

            {/* Alert Types */}
            <div>
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">
                Alert Types
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Payment Alerts</p>
                    <p className="text-sm text-white/50">M-Pesa and transaction updates</p>
                  </div>
                  <Toggle
                    enabled={settings.payment_alerts}
                    onChange={(v) => setSettings({ ...settings, payment_alerts: v })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Reminders</p>
                    <p className="text-sm text-white/50">Scheduled task notifications</p>
                  </div>
                  <Toggle
                    enabled={settings.reminder_alerts}
                    onChange={(v) => setSettings({ ...settings, reminder_alerts: v })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Marketing</p>
                    <p className="text-sm text-white/50">News and promotional content</p>
                  </div>
                  <Toggle
                    enabled={settings.marketing}
                    onChange={(v) => setSettings({ ...settings, marketing: v })}
                  />
                </div>
              </div>
            </div>

            {/* Success */}
            {success && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2 text-green-400 text-sm">
                Settings saved successfully!
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 flex gap-3 sticky bottom-0 bg-[#1a2e1a]">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg border border-white/20 text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
